import { forwardRef, useCallback, Ref, RefAttributes, type JSX, useState, useEffect } from "react";

import { InputBase, useTheme } from "@mui/material";
import { FieldValues, useController } from "react-hook-form";

import { BaseFormTextFieldProps, FormTextFieldProps, SubscribedFormTextFieldProps } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useDomId } from "@/common/hooks/useDomId";
import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useValidators } from "@/common/hooks/useValidators";
import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

function FormTextFieldInner<T extends FieldValues>(
  { control, rules, value, name, shouldUnregister, ...rest }: FormTextFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <SubscribedFormTextField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
      value={value}
    />
  ) : (
    <BaseFormTextField {...rest} name={name} value={value} ref={ref} />
  );
}

FormTextFieldInner.displayName = "FormTextField";

export const FormTextField = forwardRef(FormTextFieldInner) as <T extends FieldValues>(
  props: FormTextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function SubscribedFormTextFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormTextFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);

  const { field, fieldState } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const refCallBack = useFormInputRefs({ inputRefCallBack: field.ref, inputRef });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event) {
        onChange?.(event);
        field.onChange(event.target.value);
      }
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseFormTextField
      {...rest}
      error={rest.error || Boolean(fieldState.error)}
      helperText={rest.helperText || fieldState.error?.message}
      value={field.value ?? ""}
      inputRef={refCallBack}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-text-field"}
      name={name}
    />
  );
}

const SubscribedFormTextField = forwardRef(SubscribedFormTextFieldInner) as <T extends FieldValues>(
  props: SubscribedFormTextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseFormTextField = forwardRef<HTMLDivElement, BaseFormTextFieldProps>(
  (
    {
      id,
      inputRef,
      inputProps,
      type = "text",
      size = "medium",
      error,
      disabled,
      required,
      components,
      maxLength,
      startIcon,
      endIcon,
      startAdornment,
      endAdornment,
      value,
      defaultValue,
      onChange,
      "data-test": dataTest,
      helperText,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const { isSmallScreen } = useMediaQuery();
    const [internalValue, setInternalValue] = useState(value ?? "");
    const generatedId = useDomId();
    const inputId = id ?? generatedId;
    const helperTextId = helperText ? `${inputId}-helper-text` : undefined;
    const describedBy = [rest["aria-describedby"], helperTextId].filter(Boolean).join(" ") || undefined;

    useEffect(() => {
      setInternalValue(value ?? "");
    }, [value]);

    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(e.target.value);
        onChange?.(e);
      },
      [onChange]
    );

    if (!theme.palette.forms || !theme.palette.formsComponents?.global || !theme.palette.formsComponents?.textField) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        ...(size === "medium" && theme.typography.body2),
        ...(size === "large" && {
          ...(isSmallScreen ? theme.typography.h4 : theme.typography.h5),
          minHeight: { xs: "52px", md: "56px" },
        }),
        padding: 0,
        color: theme.palette.formsComponents.textField.textColor,
        backgroundColor: theme.palette.formsComponents.textField.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.textField.borderColor}`,
        borderRadius: theme.spacing(1),
        transition: "outline 100ms ease-out, border-color 200ms ease-out",
        outlineColor: "transparent",
        ["&.Mui-error"]: {
          borderColor: theme.palette.formsComponents.textField.errorBorderColor,
        },
        ["&:hover:not(.Mui-disabled)"]: {
          borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
        },
        ["&.Mui-focused"]: {
          borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
        ...(disabled && {
          opacity: 0.4,
        }),
        ["& .MuiInputBase-input"]: {
          ...(size === "medium" && {
            minHeight: "28px",
          }),
          ...(size === "large" && {
            minHeight: "36px",
          }),
          padding: size === "medium" ? theme.spacing(0.5, 1) : { xs: theme.spacing(0.75, 1), md: theme.spacing(1) },
          borderRadius: theme.spacing(1),
          scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`, // We leave the track transparent to avoid issues with padding
          ["&::-webkit-scrollbar-track"]: {
            background: "transparent",
          },
          ["&::-webkit-scrollbar-thumb"]: {
            background: theme.palette.formsComponents.global.scrollbarThumbColor,
          },
          ["&::placeholder"]: {
            color: theme.palette.formsComponents.textField.placeholderTextColor,
          },
          ["&::-webkit-outer-spin-button, &::-webkit-inner-spin-button"]: {
            "-webkit-appearance": "none",
            margin: 0,
          },
          ["&[type=number]"]: {
            "-moz-appearance": "textfield",
          },
          ["&.Mui-disabled"]: {
            "-webkit-text-fill-color": theme.palette.formsComponents.textField.textColor, // Override MUI default color
          },
        },
      },
      components?.input?.sx
    );

    const currentLength = internalValue?.toString()?.length ?? 0;
    const showCount = maxLength !== undefined && maxLength > 0 && currentLength !== undefined && currentLength > 0;

    return (
      <FormField
        {...getFormFieldProps(rest)}
        ref={ref}
        id={inputId}
        helperTextId={helperTextId}
        error={error}
        required={required}
        count={showCount ? currentLength : undefined}
        maxCount={showCount ? maxLength : undefined}
        disabled={disabled}
        data-test={dataTest}
        helperText={helperText}
      >
        <InputBase
          {...rest}
          id={inputId}
          aria-describedby={describedBy}
          inputRef={inputRef}
          sx={styles}
          {...(value !== undefined
            ? { value: internalValue }
            : defaultValue !== undefined
              ? { value: defaultValue }
              : {})}
          onChange={handleOnChange}
          type={type}
          error={error}
          disabled={disabled}
          startAdornment={
            startIcon ? (
              <FormIcon size="medium" sx={mergeSx({ marginInlineStart: 1 }, components?.startIcon?.sx)}>
                {startIcon}
              </FormIcon>
            ) : (
              startAdornment && (
                <FormTypography
                  variant={size === "medium" ? "body2" : isSmallScreen ? "h4" : "h5"}
                  vibe="moderate"
                  sx={mergeSx(
                    {
                      marginInlineStart: 1.5,
                      lineHeight: { xs: size === "medium" ? "28px" : "36px" }, // We need to override the typography's default line height, which is specified on a breakpoint
                    },
                    components?.startAdornment?.sx
                  )}
                >
                  {startAdornment}
                </FormTypography>
              )
            )
          }
          endAdornment={
            endIcon ? (
              <FormIcon size="medium" sx={mergeSx({ marginInlineEnd: 1 }, components?.endIcon?.sx)}>
                {endIcon}
              </FormIcon>
            ) : (
              endAdornment && (
                <FormTypography
                  variant={size === "medium" ? "body2" : isSmallScreen ? "h4" : "h5"}
                  vibe="moderate"
                  sx={mergeSx(
                    {
                      marginInlineEnd: 1.5,
                      lineHeight: { xs: size === "medium" ? "28px" : "36px" }, // We need to override the typography's default line height, which is specified on a breakpoint
                    },
                    components?.endAdornment?.sx
                  )}
                >
                  {endAdornment}
                </FormTypography>
              )
            )
          }
          inputProps={{ maxLength, "aria-required": required || undefined, ...inputProps }}
        />
      </FormField>
    );
  }
);

BaseFormTextField.displayName = "BaseFormTextField";
